import React, { useCallback } from 'react';
import { ResponsiveBar, type BarTooltipProps } from '@nivo/bar';
import { TBarGraph } from 'pages/Analytics/types';
import ChartTooltip from './ChartTooltip';
import './Graphs.scss';

type TBarGraphProps = {
  responseData: TBarGraph;
};

type BarDatum = { [key: string]: string | number };

const BarGraph: React.FC<TBarGraphProps> = ({ responseData }) => {
  const { title, xAxis, yAxis, forceOverTime } = responseData.moduleConfig.chartConfig;
  const xLabel = xAxis || 'Category';
  const yLabel = yAxis || 'Value';

  // Transform data to match the expected shape for ResponsiveBar
  let barData: BarDatum[] = [];

  if (forceOverTime) {
    // Group data by x (time) and set each name as a key
    const grouped: Record<string, Record<string, number | string>> = {};
    responseData.data.forEach((rawDatum) => {
      const xValue = new Date(rawDatum.x * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      if (!grouped[xValue]) {
        grouped[xValue] = { x: xValue };
      }
      grouped[xValue][rawDatum.name] = rawDatum.y;
    });
    barData = Object.values(grouped);
  } else {
    // Assume responseData.data is already in the correct format for bar chart
    barData = responseData.data as unknown as BarDatum[];
  }

  // Collect all unique names for keys if forceOverTime
  const keys = forceOverTime ? Array.from(new Set(responseData.data.map((d) => d.name))) : ['value'];

  const renderTooltip = useCallback(
    (bar: BarTooltipProps<BarDatum>) => (
      <ChartTooltip
        xLabel={xLabel}
        xValue={String(bar.indexValue)}
        yLabel={yLabel}
        yValue={bar.formattedValue}
        color={bar.color}
      />
    ),
    [xLabel, yLabel],
  );

  return (
    <div className="custom-analytics-graph">
      <ResponsiveBar
        label={title}
        data={barData}
        keys={keys}
        indexBy={forceOverTime ? 'x' : 'name'}
        // Extra top/side margin so the default "top"-anchored nivo tooltip has room
        // near the chart edges instead of being clipped by the tile.
        margin={{ top: 28, right: 24, bottom: 40, left: 50 }}
        padding={0.2}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        // can set our own pool of colors if we want
        colors={'#0851df'}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 5,
          // reverted the inverted axis to match the original code
          legend: xLabel,
          legendPosition: 'middle',
          legendOffset: 32,
          truncateTickAt: 8,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
          // reverted the inverted axis to match the original code
          legend: yLabel,
          legendPosition: 'middle',
          legendOffset: -40,
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        animate={true}
        tooltip={renderTooltip}
      />
    </div>
  );
};

export default BarGraph;
